/**
 * 前台商城通用布局组件 - 中式典雅风格
 * 配色：宣纸白 (#F8F6F1)、茶褐 (#7D6B55)、竹青 (#6B8E7C)、朱砂红 (#C44536)
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Layout,
  Menu,
  Modal,
  Badge,
  Button,
  Dropdown,
  Space,
  Typography,
  Row,
  Col,
  Spin,
  Drawer,
  Grid,
  Divider,
} from 'antd';
import {
  ShoppingCartOutlined,
  UserOutlined,
  DownOutlined,
  LogoutOutlined,
  OrderedListOutlined,
  HomeOutlined,
  MenuOutlined,
  AppstoreOutlined,
  WechatOutlined,
  MailOutlined ,
  ToolOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { productApi } from '../../api/index';
import AnnouncementBar from './AnnouncementBar';
import './storefront.css';

const { Header, Content, Footer } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

// 中式配色
const COLORS = {
  paper: '#F8F6F1',      // 宣纸白
  teaBrown: '#7D6B55',   // 茶褐
  bambooGreen: '#6B8E7C', // 竹青
  cinnabar: '#C44536',   // 朱砂红
  ink: '#2C2C2C',        // 墨色
  lightInk: '#5A5A5A',   // 淡墨
};

export default function StorefrontLayout({ children }) {
  const navigate = useNavigate();
  const { user, isLoggedIn, logout } = useAuth();
  const { cart, refreshCart } = useCart();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // 分类列表状态
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(false);

  // 店铺设置状态
  const [storeSetting, setStoreSetting] = useState(() => {
    // 从 localStorage 读取缓存的店铺设置
    const cached = localStorage.getItem('storeSetting');
    return cached ? JSON.parse(cached) : {};
  });
  const [logoError, setLogoError] = useState(false);

  // 侧边导航抽屉
  const [drawerVisible, setDrawerVisible] = useState(false);

  // 初始化：拉取分类列表 + 刷新购物车 + 获取店铺设置 + 加载聊天插件
  useEffect(() => {
    fetchCategories();
    refreshCart();
    fetchStoreSetting();
    // 加载 Chatway 聊天插件
    const script = document.createElement('script');
    script.id = 'chatway';
    script.src = 'https://cdn.chatway.app/widget.js?id=9Eleg5gstPcx';
    script.async = true;
    document.body.appendChild(script);
    // 清理函数
    return () => {
      const existingScript = document.getElementById('chatway');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 加载所有分类 */
  const fetchCategories = async () => {
    setCatLoading(true);
    try {
      const data = await productApi.listCategories();
      setCategories(data || []);
    } catch {
      setCategories([]);
    } finally {
      setCatLoading(false);
    }
  };

  /** 获取店铺设置 */
  const fetchStoreSetting = async () => {
    try {
      const data = await productApi.getSettings();
      console.log('店铺设置:', data);
      setStoreSetting(data);
      setLogoError(false);
      // 缓存到 localStorage
      localStorage.setItem('storeSetting', JSON.stringify(data));
    } catch (err) {
      console.error('获取店铺设置失败:', err);
      setStoreSetting(null);
    }
  };

  /** Logo 图片加载失败时切换到文字显示 */
  const handleLogoError = () => {
    setLogoError(true);
  };

  // 动态设置网站标题和描述
  useEffect(() => {
    if (storeSetting) {
      document.title = storeSetting.storeName || '悦来悦好';
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', storeSetting.description || '');
      } else {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = storeSetting.description || '';
        document.head.appendChild(meta);
      }
    }
  }, [storeSetting]);

  /** 退出登录 */
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // 已登录时的账户下拉菜单
  const accountMenuItems = [
    {
      key: 'orders',
      icon: <OrderedListOutlined />,
      label: <Link to="/orders">我的订单</Link>,
    },
    {
      key: 'account',
      icon: <UserOutlined />,
      label: <Link to="/account">账户设置</Link>,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: COLORS.paper, overflowX: 'hidden' }}>
      {/* ===== 公告栏 ===== */}
      {storeSetting?.announcementEnabled && (
        <AnnouncementBar announcement={storeSetting?.announcement} />
      )}

      {/* ===== 网站维护弹窗：开启后不允许前台用户关闭 ===== */}
      <Modal
        open={storeSetting?.maintenanceEnabled === true}
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={null}
        centered
        width={520}
        zIndex={2000}
        className="maintenance-modal"
        wrapClassName="maintenance-modal-wrap"
      >
        <div className="maintenance-panel">
          <div className="maintenance-seal" aria-hidden="true">
            <ToolOutlined />
            <span>维护</span>
          </div>
          <div className="maintenance-kicker">SERVICE PAUSED</div>
          <h2 className="maintenance-title">网站维护中</h2>
          <div className="maintenance-divider" />
          <p className="maintenance-message">
            {storeSetting?.maintenanceMessage || '网站正在维护升级，请稍后再访问。'}
          </p>
          <div className="maintenance-note">感谢您的耐心等待</div>
        </div>
      </Modal>

      {/* ===== 顶部 Header ===== */}
      <Header
        className="storefront-header"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: `linear-gradient(135deg, ${COLORS.paper} 0%, ${COLORS.teaBrown}08 100%)`,
          borderBottom: `1px solid ${COLORS.teaBrown}15`,
          padding: '0 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 80,
          minHeight: 'auto',
        }}
      >
        <div style={{ width: '100%', maxWidth: 1200, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* 左侧：Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            {storeSetting?.logoUrl && !logoError ? (
              <img
                src={storeSetting.logoUrl}
                alt={storeSetting?.storeName || '悦来悦好'}
                style={{
                  maxHeight: isMobile ? 60 : 100,
                  width: 'auto',
                  objectFit: 'contain',
                }}
                onError={handleLogoError}
              />
            ) : (
              <Text
                strong
                style={{
                  fontSize: isMobile ? 22 : 28,
                  color: COLORS.ink,
                  letterSpacing: '0.3em',
                  fontWeight: 300,
                }}
              >
                {storeSetting?.storeName || '悦来悦好'}
              </Text>
            )}
          </Link>

          {/* 右侧：购物车 + 用户按钮 */}
          <Space size={16} align="center">
            {/* 购物车图标（含徽标显示商品数） */}
            <Badge count={cart?.itemCount || 0} showZero={false}>
              <Button
                type="text"
                icon={<ShoppingCartOutlined style={{ fontSize: 24, color: COLORS.teaBrown }} />}
                onClick={() => navigate('/cart')}
              />
            </Badge>

            {/* 登录状态：已登录显示用户名下拉；未登录显示登录按钮 */}
            {isLoggedIn ? (
              <Dropdown menu={{ items: accountMenuItems }} placement="bottomRight" arrow>
                <Button type="text" icon={<UserOutlined style={{ fontSize: 20, color: COLORS.teaBrown }} />}>
                  <Space>
                    <Text style={{ fontSize: 14, color: COLORS.ink }}>{(user?.lastName && user?.firstName) ? `${user.lastName} ${user.firstName}` : (user?.firstName || user?.email || '账户')}</Text>
                    <DownOutlined style={{ fontSize: 10, color: COLORS.teaBrown }} />
                  </Space>
                </Button>
              </Dropdown>
            ) : (
              <Button
                type="primary"
                onClick={() => navigate('/login')}
                style={{ background: COLORS.teaBrown, borderColor: COLORS.teaBrown }}
              >
                登录
              </Button>
            )}
          </Space>
        </div>
      </Header>

      {/* ===== 侧边导航抽屉 ===== */}
      <Drawer
        title={
          <Text strong style={{ fontSize: 16, color: COLORS.ink, letterSpacing: '0.1em' }}>
            商品分类
          </Text>
        }
        placement="left"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={280}
        headerStyle={{ borderBottom: `1px solid ${COLORS.teaBrown}15`, background: `${COLORS.paper}` }}
        bodyStyle={{ background: COLORS.paper }}
      >
        <Menu
          mode="vertical"
          style={{ border: 'none', background: COLORS.paper }}
          itemStyle={{ borderRadius: 8, marginBottom: 4 }}
        >
          <Menu.Item
            key="home"
            icon={<HomeOutlined style={{ color: COLORS.bambooGreen }} />}
            onClick={() => { navigate('/'); setDrawerVisible(false); }}
            style={{ color: COLORS.ink }}
          >
            首页
          </Menu.Item>
          {catLoading ? (
            <div style={{ padding: 16, textAlign: 'center' }}><Spin size="small" /></div>
          ) : (
            categories.map((cat) => (
              <Menu.Item
                key={cat.id}
                icon={<AppstoreOutlined style={{ color: COLORS.bambooGreen }} />}
                style={{ color: COLORS.ink }}
              >
                <Link to={`/categories/${cat.slug}`} style={{ color: 'inherit' }}>{cat.name}</Link>
              </Menu.Item>
            ))
          )}
          <Menu.Divider style={{ borderColor: `${COLORS.teaBrown}20` }} />
          <Menu.Item
            key="cart"
            icon={<ShoppingCartOutlined style={{ color: COLORS.bambooGreen }} />}
            onClick={() => { navigate('/cart'); setDrawerVisible(false); }}
            style={{ color: COLORS.ink }}
          >
            购物车 {cart?.itemCount ? `(${cart.itemCount})` : ''}
          </Menu.Item>
          {isLoggedIn ? (
            <>
              <Menu.Item
                key="orders"
                icon={<OrderedListOutlined style={{ color: COLORS.bambooGreen }} />}
                onClick={() => { navigate('/orders'); setDrawerVisible(false); }}
                style={{ color: COLORS.ink }}
              >
                我的订单
              </Menu.Item>
              <Menu.Item
                key="account"
                icon={<UserOutlined style={{ color: COLORS.bambooGreen }} />}
                onClick={() => { navigate('/account'); setDrawerVisible(false); }}
                style={{ color: COLORS.ink }}
              >
                账户设置
              </Menu.Item>
              <Menu.Item
                key="logout"
                icon={<LogoutOutlined style={{ color: COLORS.cinnabar }} />}
                onClick={() => { handleLogout(); setDrawerVisible(false); }}
                style={{ color: COLORS.cinnabar }}
              >
                退出登录
              </Menu.Item>
            </>
          ) : (
            <Menu.Item
              key="login"
              icon={<UserOutlined style={{ color: COLORS.bambooGreen }} />}
              onClick={() => { navigate('/login'); setDrawerVisible(false); }}
              style={{ color: COLORS.ink }}
            >
              登录
            </Menu.Item>
          )}
        </Menu>
      </Drawer>

      {/* ===== 页面内容区域 ===== */}
      <Content style={{
        minHeight: isMobile ? 'calc(100vh - 140px)' : 'calc(100vh - 150px - 70px)',
        paddingBottom: isMobile ? 0 : 24,
        background: COLORS.paper,
      }}>
        {children}
      </Content>

      {/* ===== 底部 Footer ===== */}
      <Footer style={{
        textAlign: 'center',
        background: `${COLORS.teaBrown}08`,
        borderTop: `1px solid ${COLORS.teaBrown}15`,
        padding: '32px 0 24px',
        marginBottom: isMobile ? 60 : 0,
      }}>
        {/* 联系我们模块 */}
        <div style={{ marginBottom: 24 }}>
          <Text strong style={{ color: COLORS.ink, fontSize: 14, display: 'block', marginBottom: 16 }}>
            联系我们
          </Text>
          <Space size={32} style={{ justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 24,
                  background: '#07C160',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 8px',
                }}
              >
                <WechatOutlined style={{ fontSize: 24, color: '#fff' }} />
              </div>
              <Text style={{ color: COLORS.lightInk, fontSize: 12 }}>ylyh_support</Text>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 24,
                  background: '#25D366',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 8px',
                }}
              >
                <MailOutlined style={{ fontSize: 24, color: '#fff' }} />
              </div>
              <Text style={{ color: COLORS.lightInk, fontSize: 12 }}>info@ylyh666.com</Text>
            </div>
          </Space>
        </div>

        <Divider style={{ borderColor: `${COLORS.teaBrown}15`, margin: '16px 0' }} />

        {/* 版权和链接 */}
        <Row justify="center" gutter={[32, 8]}>
          <Col>
            <Text style={{ color: COLORS.lightInk, fontSize: 13 }}>
              © 2026 {storeSetting?.storeName || 'YK2 Shop'}. 保留所有权利。
            </Text>
          </Col>
        </Row>
        <Row justify="center" style={{ marginTop: 8 }}>
          <Col>
            <Space split={<span style={{ color: `${COLORS.teaBrown}30` }}>·</span>}>
              <Link to="/" style={{ color: COLORS.lightInk, fontSize: 12 }}>首页</Link>
              <Link to="/cart" style={{ color: COLORS.lightInk, fontSize: 12 }}>购物车</Link>
              <Link to="/orders" style={{ color: COLORS.lightInk, fontSize: 12 }}>我的订单</Link>
              <Link to="/account" style={{ color: COLORS.lightInk, fontSize: 12 }}>账户设置</Link>
            </Space>
          </Col>
        </Row>
      </Footer>

      {/* ===== 移动端底部固定导航栏 ===== */}
      <div className="mobile-bottom-nav" style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: 60,
        background: COLORS.paper,
        borderTop: `1px solid ${COLORS.teaBrown}15`,
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        zIndex: 99,
        boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
      }}>
        <Button
          type="text"
          icon={<HomeOutlined style={{ fontSize: 20, color: COLORS.teaBrown }} />}
          onClick={() => navigate('/')}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
        >
          <Text style={{ fontSize: 10, marginTop: 2, color: COLORS.lightInk }}>首页</Text>
        </Button>
        <Button
          type="text"
          icon={<AppstoreOutlined style={{ fontSize: 20, color: COLORS.teaBrown }} />}
          onClick={() => setDrawerVisible(true)}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
        >
          <Text style={{ fontSize: 10, marginTop: 2, color: COLORS.lightInk }}>菜单</Text>
        </Button>
        <Badge count={cart?.itemCount || 0} showZero={false}>
          <Button
            type="text"
            icon={<ShoppingCartOutlined style={{ fontSize: 20, color: COLORS.teaBrown }} />}
            onClick={() => navigate('/cart')}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
          >
            <Text style={{ fontSize: 10, marginTop: 2, color: COLORS.lightInk }}>购物车</Text>
          </Button>
        </Badge>
        {isLoggedIn ? (
          <Button
            type="text"
            icon={<UserOutlined style={{ fontSize: 20, color: COLORS.teaBrown }} />}
            onClick={() => navigate('/account')}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
          >
            <Text style={{ fontSize: 10, marginTop: 2, color: COLORS.lightInk }}>账户</Text>
          </Button>
        ) : (
          <Button
            type="text"
            icon={<UserOutlined style={{ fontSize: 20, color: COLORS.teaBrown }} />}
            onClick={() => navigate('/login')}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
          >
            <Text style={{ fontSize: 10, marginTop: 2, color: COLORS.lightInk }}>登录</Text>
          </Button>
        )}
      </div>
    </Layout>
  );
}
